import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader } from "../../shared/components/Loader";
import {
  CheckIcon,
  CloseIcon,
  CollapseArrowIcon,
  SmallCloseIcon,
} from "../protocols/NewFiltersPanelIcons";
import type { TrialFiltersApiResponse } from "./trialService";
import { getAllLabel } from "../../shared/utils/filterLabels";
import "./TrialFiltersPanel.less";

export interface TrialFiltersState {
  status: string[];
  type: string[];
  crop: string[];
  fields: string[];
  country: string[];
  timeRange: {
    from?: string;
    to?: string;
  };
  creator: string[];
  assignee: string[];
}

type MultiSelectFilterKey = Exclude<keyof TrialFiltersState, "timeRange">;

interface OptionWithIdAndName {
  id: string;
  name: string;
}

interface TrialFiltersPanelProps {
  open: boolean;
  onClose: () => void;
  onApply?: (filters: TrialFiltersState) => void;
  onCancel?: () => void;
  data?: any[];
  filterOptions?: TrialFiltersApiResponse | null;
  initialFilters?: Partial<TrialFiltersState>;
  loading?: boolean;
}

const EMPTY_FILTERS: TrialFiltersState = {
  status: [],
  type: [],
  crop: [],
  fields: [],
  country: [],
  timeRange: {},
  creator: [],
  assignee: [],
};

const DEFAULT_COLLAPSED: Record<keyof TrialFiltersState, boolean> = {
  status: true,
  type: true,
  crop: true,
  fields: true,
  country: true,
  timeRange: true,
  creator: true,
  assignee: true,
};

const DEFAULT_STATUS_OPTIONS = ["Activated", "Ready", "Draft"];

/**
 * Type options use:
 * - id: backend value sent in filters.type
 * - name: UI label shown in the filter panel
 */
const DEFAULT_TYPE_OPTIONS: OptionWithIdAndName[] = [
  {
    id: "seed",
    name: "Seeds",
  },
  {
    id: "biological",
    name: "CP & Biologicals",
  },
  {
    id: "nutrient",
    name: "Nutrients",
  },
];

const normalizeInitialFilters = (
  initialFilters?: Partial<TrialFiltersState>
): TrialFiltersState => ({
  ...EMPTY_FILTERS,
  ...(initialFilters ?? {}),
  timeRange: initialFilters?.timeRange ?? {},
});

const getUniqueSortedValues = (
  data: any[],
  keys: string[],
  arrayValue = false
): string[] => {
  const values = new Set<string>();

  for (const record of data ?? []) {
    for (const key of keys) {
      const value = record?.[key];

      if (!value) continue;

      if (arrayValue && Array.isArray(value)) {
        value.forEach((item) => {
          if (item) values.add(String(item));
        });
      } else if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item) values.add(String(item));
        });
      } else {
        values.add(String(value));
      }
    }
  }

  return Array.from(values).sort((a, b) => a.localeCompare(b));
};

const getUniqueUsers = (
  data: any[],
  idKeys: string[],
  nameKeys: string[]
): OptionWithIdAndName[] => {
  const users = new Map<string, string>();

  for (const record of data ?? []) {
    let id: string | undefined;
    let name: string | undefined;

    for (const key of idKeys) {
      const value = record?.[key];

      if (value) {
        id = String(value);
        break;
      }
    }

    for (const key of nameKeys) {
      const value = record?.[key];

      if (value) {
        name = String(value);
        break;
      }
    }

    const userId = id ?? name;

    if (userId) {
      users.set(String(userId), name ?? String(userId));
    }
  }

  return Array.from(users.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

const normalizeStringOptions = (values?: string[]): string[] => {
  return Array.from(
    new Set((values ?? []).filter(Boolean).map((value) => String(value)))
  ).sort((a, b) => a.localeCompare(b));
};

const normalizeIdNameOptions = (
  values?: Array<{ id?: string; name?: string }>
): OptionWithIdAndName[] => {
  const options = new Map<string, string>();

  for (const value of values ?? []) {
    if (!value?.id) continue;

    options.set(
      String(value.id),
      value.name ? String(value.name) : String(value.id)
    );
  }

  return Array.from(options.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export default function TrialFiltersPanel({
  open,
  onClose,
  onApply,
  onCancel,
  data = [],
  filterOptions,
  initialFilters,
  loading = false,
}: TrialFiltersPanelProps): JSX.Element | null {
  const [localFilters, setLocalFilters] = useState<TrialFiltersState>(
    normalizeInitialFilters(initialFilters)
  );

  const [collapsed, setCollapsed] =
    useState<Record<keyof TrialFiltersState, boolean>>(DEFAULT_COLLAPSED);

  const previousOpenRef = useRef(open);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const wasClosed = !previousOpenRef.current;
    const isOpening = open && wasClosed;

    if (isOpening) {
      setLocalFilters(normalizeInitialFilters(initialFilters));
      setCollapsed(DEFAULT_COLLAPSED);
    }

    previousOpenRef.current = open;
  }, [open, initialFilters]);

  const options = useMemo(() => {
    const apiFields = normalizeIdNameOptions(filterOptions?.fields);
    const apiCreators = normalizeIdNameOptions(filterOptions?.created_by);
    const apiAssignees = normalizeIdNameOptions(filterOptions?.assignees);

    return {
      status: DEFAULT_STATUS_OPTIONS,

      /**
       * Type is constant.
       * It displays friendly labels but stores backend ids.
       */
      type: DEFAULT_TYPE_OPTIONS,

      crop:
        filterOptions?.crop !== undefined
          ? normalizeStringOptions(filterOptions.crop)
          : getUniqueSortedValues(data, ["crop"]),

      fields:
        filterOptions?.fields !== undefined
          ? apiFields
          : getUniqueSortedValues(data, ["field_name", "field_names"]).map(
              (field) => ({
                id: field,
                name: field,
              })
            ),

      country:
        filterOptions?.country !== undefined
          ? normalizeStringOptions(filterOptions.country)
          : getUniqueSortedValues(data, ["country"]),

      creators:
        filterOptions?.created_by !== undefined
          ? apiCreators
          : getUniqueUsers(
              data,
              ["created_by", "creator_id", "created_by_id"],
              ["created_by_name", "creator_name"]
            ),

      assignees:
        filterOptions?.assignees !== undefined
          ? apiAssignees
          : getUniqueUsers(
              data,
              ["assignee_id", "assigned_to", "assignee"],
              ["assignee_name", "assigned_to_name"]
            ),
    };
  }, [data, filterOptions]);

  useEffect(() => {
    if (loading) return;

    setLocalFilters((previousFilters) => ({
      ...previousFilters,
      status: previousFilters.status.filter((status) =>
        options.status.includes(status)
      ),
      type: previousFilters.type.filter((type) =>
        options.type.some((option) => option.id === type)
      ),
      crop: previousFilters.crop.filter((crop) => options.crop.includes(crop)),
      fields: previousFilters.fields.filter((field) =>
        options.fields.some((option) => option.id === field)
      ),
      country: previousFilters.country.filter((country) =>
        options.country.includes(country)
      ),
      timeRange: previousFilters.timeRange,
      creator: previousFilters.creator.filter((id) =>
        options.creators.some((creator) => creator.id === id)
      ),
      assignee: previousFilters.assignee.filter((id) =>
        options.assignees.some((assignee) => assignee.id === id)
      ),
    }));
  }, [loading, options]);

  const toggleOption = (section: MultiSelectFilterKey, value: string): void => {
    setLocalFilters((previousFilters) => {
      const selectedValues = new Set(previousFilters[section]);

      if (selectedValues.has(value)) {
        selectedValues.delete(value);
      } else {
        selectedValues.add(value);
      }

      return {
        ...previousFilters,
        [section]: Array.from(selectedValues),
      };
    });
  };

  const toggleAll = (
    section: MultiSelectFilterKey,
    allValues: string[]
  ): void => {
    setLocalFilters((previousFilters) => {
      const selectedValues = new Set(previousFilters[section]);

      const isAllSelected =
        allValues.length > 0 &&
        allValues.every((value) => selectedValues.has(value));

      return {
        ...previousFilters,
        [section]: isAllSelected ? [] : [...allValues],
      };
    });
  };

  const clearSection = (section: keyof TrialFiltersState): void => {
    setLocalFilters((previousFilters) => ({
      ...previousFilters,
      [section]: section === "timeRange" ? {} : [],
    }));
  };

  const selectedCount = (section: keyof TrialFiltersState): number => {
    if (section === "timeRange") {
      return timeRangeHasValue(localFilters.timeRange) ? 1 : 0;
    }

    return localFilters[section].length;
  };

  const updateTimeRange = (key: "from" | "to", value: string): void => {
    setLocalFilters((previousFilters) => ({
      ...previousFilters,
      timeRange: {
        ...previousFilters.timeRange,
        [key]: value,
      },
    }));
  };

  const handleApply = (): void => {
    onApply?.(localFilters);
    onClose();
  };

  const handleCancel = (): void => {
    setLocalFilters(normalizeInitialFilters(initialFilters));
    onCancel?.();
    onClose();
  };

  if (!open) return null;

  const panel = (
    <div aria-hidden={!open} className="trial-filters-panel">
      <div onClick={onClose} className="trial-filters-panel__backdrop" />

      <aside
        role="dialog"
        aria-modal="true"
        className="trial-filters-panel__drawer"
      >
        <header className="trial-filters-panel__header">
          <div className="trial-filters-panel__title">Filters</div>

          <button
            type="button"
            aria-label="Close filters"
            onClick={onClose}
            className="trial-filters-panel__close-button"
          >
            <CloseIcon />
          </button>
        </header>

        <div className="trial-filters-panel__body">
          {loading ? (
            <Loader mask />
          ) : (
            <>
              {renderSection(
                "Status",
                "status",
                options.status,
                localFilters,
                collapsed,
                setCollapsed,
                selectedCount,
                toggleAll,
                toggleOption,
                clearSection
              )}

              {renderIdNameSection(
                "Type",
                "type",
                options.type,
                localFilters,
                collapsed,
                setCollapsed,
                selectedCount,
                toggleAll,
                toggleOption,
                clearSection
              )}

              {renderSection(
                "Crop",
                "crop",
                options.crop,
                localFilters,
                collapsed,
                setCollapsed,
                selectedCount,
                toggleAll,
                toggleOption,
                clearSection
              )}

              {renderIdNameSection(
                "Fields",
                "fields",
                options.fields,
                localFilters,
                collapsed,
                setCollapsed,
                selectedCount,
                toggleAll,
                toggleOption,
                clearSection
              )}

              {renderSection(
                "Country",
                "country",
                options.country,
                localFilters,
                collapsed,
                setCollapsed,
                selectedCount,
                toggleAll,
                toggleOption,
                clearSection
              )}

              {renderTimeRangeSection(
                "Time Range",
                "timeRange",
                localFilters,
                collapsed,
                setCollapsed,
                selectedCount,
                clearSection,
                updateTimeRange
              )}

              {renderIdNameSection(
                "Creator",
                "creator",
                options.creators,
                localFilters,
                collapsed,
                setCollapsed,
                selectedCount,
                toggleAll,
                toggleOption,
                clearSection
              )}

              {renderIdNameSection(
                "Assignee",
                "assignee",
                options.assignees,
                localFilters,
                collapsed,
                setCollapsed,
                selectedCount,
                toggleAll,
                toggleOption,
                clearSection
              )}

              <div className="trial-filters-panel__spacer" />
            </>
          )}
        </div>

        <footer className="trial-filters-panel__footer">
          <button
            type="button"
            onClick={handleCancel}
            className="trial-filters-panel__button trial-filters-panel__button--cancel"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleApply}
            className="trial-filters-panel__button trial-filters-panel__button--apply"
          >
            Apply
          </button>
        </footer>
      </aside>
    </div>
  );

  return createPortal(panel, document.body);
}

function timeRangeHasValue(timeRange: { from?: string; to?: string }): boolean {
  return Boolean(timeRange.from ?? timeRange.to);
}

function CollapseArrow({ open }: { open: boolean }): JSX.Element {
  return (
    <div
      className={`trial-filters-panel__collapse-arrow ${
        open
          ? "trial-filters-panel__collapse-arrow--open"
          : "trial-filters-panel__collapse-arrow--closed"
      }`}
      aria-hidden="true"
    >
      <CollapseArrowIcon />
    </div>
  );
}

function FilterBadge({
  label,
  title,
  onClear,
}: {
  label: string;
  title: string;
  onClear: () => void;
}): JSX.Element {
  return (
    <div className="trial-filters-panel__badge-wrapper">
      <div className="trial-filters-panel__filter-pill">
        <span className="trial-filters-panel__filter-pill-label">{label}</span>

        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onClear();
          }}
          aria-label={`Clear ${title}`}
          className="trial-filters-panel__filter-pill-clear"
        >
          <SmallCloseIcon />
        </button>
      </div>
    </div>
  );
}

function renderSection(
  title: string,
  keyName: MultiSelectFilterKey,
  options: string[],
  localFilters: TrialFiltersState,
  collapsed: Record<keyof TrialFiltersState, boolean>,
  setCollapsed: React.Dispatch<
    React.SetStateAction<Record<keyof TrialFiltersState, boolean>>
  >,
  selectedCount: (section: keyof TrialFiltersState) => number,
  toggleAll: (section: MultiSelectFilterKey, values: string[]) => void,
  toggleOption: (section: MultiSelectFilterKey, value: string) => void,
  clearSection: (section: keyof TrialFiltersState) => void
): JSX.Element {
  const openState = !collapsed[keyName];
  const count = selectedCount(keyName);

  const isAllChecked =
    options.length > 0 &&
    options.every((option) => localFilters[keyName].includes(option));

  const shouldShowCountBadge = isAllChecked || count > 0;
  const countBadgeLabel = isAllChecked ? "All" : `(${count})`;

  const toggleCollapsed = (): void => {
    setCollapsed((currentCollapsed) => ({
      ...currentCollapsed,
      [keyName]: !currentCollapsed[keyName],
    }));
  };

  return (
    <div key={String(keyName)} className="trial-filters-panel__section">
      <div
        role="button"
        tabIndex={0}
        onClick={toggleCollapsed}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleCollapsed();
          }
        }}
        aria-expanded={openState}
        aria-label={`${openState ? "Collapse" : "Expand"} ${title}`}
        className="trial-filters-panel__section-header"
      >
        <div className="trial-filters-panel__section-title-wrapper">
          <div className="trial-filters-panel__section-title">{title}</div>

          {shouldShowCountBadge && (
            <FilterBadge
              label={countBadgeLabel}
              title={title}
              onClear={() => {
                clearSection(keyName);
              }}
            />
          )}
        </div>

        <CollapseArrow open={openState} />
      </div>

      {openState && (
        <div>
          {options.length > 0 && (
            <div
              onClick={() => {
                toggleAll(keyName, options);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  toggleAll(keyName, options);
                }
              }}
              className="trial-filters-panel__all-option-row"
            >
              <Square checked={isAllChecked} variant="all" />

              <div className="trial-filters-panel__option-text">
                {getAllLabel(title)}
              </div>
            </div>
          )}

          <div className="trial-filters-panel__options-list">
            {options.map((option) => {
              const checked = localFilters[keyName].includes(option);

              return (
                <div
                  key={option}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    toggleOption(keyName, option);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      toggleOption(keyName, option);
                    }
                  }}
                  className="trial-filters-panel__option-row"
                >
                  <Square checked={checked} />

                  <div className="trial-filters-panel__option-text">
                    {option}
                  </div>
                </div>
              );
            })}

            {options.length === 0 && (
              <div className="trial-filters-panel__no-options">No options</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function renderTimeRangeSection(
  title: string,
  keyName: "timeRange",
  localFilters: TrialFiltersState,
  collapsed: Record<keyof TrialFiltersState, boolean>,
  setCollapsed: React.Dispatch<
    React.SetStateAction<Record<keyof TrialFiltersState, boolean>>
  >,
  selectedCount: (section: keyof TrialFiltersState) => number,
  clearSection: (section: keyof TrialFiltersState) => void,
  updateTimeRange: (key: "from" | "to", value: string) => void
): JSX.Element {
  const openState = !collapsed[keyName];
  const count = selectedCount(keyName);
  const shouldShowCountBadge = count > 0;

  const toggleCollapsed = (): void => {
    setCollapsed((currentCollapsed) => ({
      ...currentCollapsed,
      [keyName]: !currentCollapsed[keyName],
    }));
  };

  return (
    <div key={String(keyName)} className="trial-filters-panel__section">
      <div
        role="button"
        tabIndex={0}
        onClick={toggleCollapsed}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleCollapsed();
          }
        }}
        aria-expanded={openState}
        aria-label={`${openState ? "Collapse" : "Expand"} ${title}`}
        className="trial-filters-panel__section-header"
      >
        <div className="trial-filters-panel__section-title-wrapper">
          <div className="trial-filters-panel__section-title">{title}</div>

          {shouldShowCountBadge && (
            <FilterBadge
              label={`(${count})`}
              title={title}
              onClear={() => {
                clearSection(keyName);
              }}
            />
          )}
        </div>

        <CollapseArrow open={openState} />
      </div>

      {openState && (
        <div className="trial-filters-panel__date-range">
          <div className="trial-filters-panel__date-field">
            <label
              htmlFor="trial-filter-from-date"
              className="trial-filters-panel__date-label"
            >
              From
            </label>

            <input
              id="trial-filter-from-date"
              type="date"
              value={localFilters.timeRange.from ?? ""}
              onChange={(event) => {
                updateTimeRange("from", event.target.value);
              }}
              className="trial-filters-panel__date-input"
            />
          </div>

          <div className="trial-filters-panel__date-field">
            <label
              htmlFor="trial-filter-to-date"
              className="trial-filters-panel__date-label"
            >
              To
            </label>

            <input
              id="trial-filter-to-date"
              type="date"
              value={localFilters.timeRange.to ?? ""}
              onChange={(event) => {
                updateTimeRange("to", event.target.value);
              }}
              className="trial-filters-panel__date-input"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function renderIdNameSection(
  title: string,
  keyName: MultiSelectFilterKey,
  options: OptionWithIdAndName[],
  localFilters: TrialFiltersState,
  collapsed: Record<keyof TrialFiltersState, boolean>,
  setCollapsed: React.Dispatch<
    React.SetStateAction<Record<keyof TrialFiltersState, boolean>>
  >,
  selectedCount: (section: keyof TrialFiltersState) => number,
  toggleAll: (section: MultiSelectFilterKey, values: string[]) => void,
  toggleOption: (section: MultiSelectFilterKey, value: string) => void,
  clearSection: (section: keyof TrialFiltersState) => void
): JSX.Element {
  const ids = options.map((option) => option.id);
  const openState = !collapsed[keyName];
  const count = selectedCount(keyName);

  const isAllChecked =
    ids.length > 0 && ids.every((id) => localFilters[keyName].includes(id));

  const shouldShowCountBadge = isAllChecked || count > 0;
  const countBadgeLabel = isAllChecked ? "All" : `(${count})`;

  const toggleCollapsed = (): void => {
    setCollapsed((currentCollapsed) => ({
      ...currentCollapsed,
      [keyName]: !currentCollapsed[keyName],
    }));
  };

  return (
    <div key={String(keyName)} className="trial-filters-panel__section">
      <div
        role="button"
        tabIndex={0}
        onClick={toggleCollapsed}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleCollapsed();
          }
        }}
        aria-expanded={openState}
        aria-label={`${openState ? "Collapse" : "Expand"} ${title}`}
        className="trial-filters-panel__section-header"
      >
        <div className="trial-filters-panel__section-title-wrapper">
          <div className="trial-filters-panel__section-title">{title}</div>

          {shouldShowCountBadge && (
            <FilterBadge
              label={countBadgeLabel}
              title={title}
              onClear={() => {
                clearSection(keyName);
              }}
            />
          )}
        </div>

        <CollapseArrow open={openState} />
      </div>

      {openState && (
        <div>
          {options.length > 0 && (
            <div
              onClick={() => {
                toggleAll(keyName, ids);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  toggleAll(keyName, ids);
                }
              }}
              className="trial-filters-panel__all-option-row"
            >
              <Square checked={isAllChecked} variant="all" />

              <div className="trial-filters-panel__option-text">
                {getAllLabel(title)}
              </div>
            </div>
          )}

          <div className="trial-filters-panel__options-list">
            {options.map((option) => {
              const checked = localFilters[keyName].includes(option.id);

              return (
                <div
                  key={option.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    toggleOption(keyName, option.id);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      toggleOption(keyName, option.id);
                    }
                  }}
                  className="trial-filters-panel__option-row"
                >
                  <Square checked={checked} />

                  <div className="trial-filters-panel__option-text">
                    {option.name}
                  </div>
                </div>
              );
            })}

            {options.length === 0 && (
              <div className="trial-filters-panel__no-options">No options</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Square({
  checked,
  variant = "default",
}: {
  checked: boolean;
  variant?: "default" | "all";
}): JSX.Element {
  const squareClassName = checked
    ? variant === "all"
      ? "trial-filters-panel__square trial-filters-panel__square--all-checked"
      : "trial-filters-panel__square trial-filters-panel__square--checked"
    : "trial-filters-panel__square trial-filters-panel__square--unchecked";

  return (
    <div className="trial-filters-panel__square-wrapper">
      <div className={squareClassName}>
        {checked ? (
          variant === "all" ? (
            <div className="trial-filters-panel__all-inner-square" />
          ) : (
            <CheckIcon />
          )
        ) : null}
      </div>
    </div>
  );
}
